/****************************************************
 * Module Name:  lotIntimationMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import Constants from "../../../utility/constants";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip, Stack, Typography } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../../common/dataGridCellExpand";
import Utility from "../../../utility/utility";
import CommonSnackbar from "../../common/commonSnackBar";
import LotIntimationProxy from "../../../utility/proxy/manager/manufacturingProxy/lotIntimationProxy";
import AddLotIntimationModal from "./addLotIntimation";
import AddLotCreationModal from "./addLotCreationModal";
import CloseIcon from "@mui/icons-material/Close";
import LotCreationProxy from "../../../utility/proxy/manager/manufacturingProxy/lotCreationProxy";
import DashboardProxy from "../../../utility/proxy/manager/dashboardProxy";
import NoPermission from "../../common/noPermission";
import UploadAndViewLotDocumentModal from "../../manufactureMaster/uploadAndViewLotDocumentModal";
import { IoDocumentAttach } from "react-icons/io5";
import PageTitle from "../../common/pageTitle";

export class LotIntimationMaster extends Component {
  constructor(props) {
    super(props);

    this.state = {
      lotIntimations:
        this.props.editButton && this.props.vendorData
          ? this.props.vendorData
          : [],
      showAddLotIntimationModal: false,
      showAddLotCreationModal: false,
      lotid: "",
      showDetailModal: false,
      showAddLotDocumentModal: false,
      sourceData: {},
      companyId: "",
      vendorId: "",
      errorSnackbar: false,
      uploadSuccessSnackbar: false,
      uploadLoader: false,
      uploadResponse: [],
      errorMessage: "",
      snackbarVariant: "",
      loadingButton: false,
      accesDenied: false,
      intimationid: "",
      filterValues: {},

      totalLot: 0,
      totalPcs: 0,
      totalCarats: 0,

      showSnackbar: false,
      snackbarMessage: "",
      columnNames: [],

      showLotIntimationLoadSpinner:
        this.props.editButton && this.props.vendorData ? false : true,
      local_Permission: Constants.USER_PERMISSION.filter(
        (iPermission) =>
          iPermission.permissionid ===
          Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOTINTIMATION
      ),
      lotCreation_Permission: Constants.USER_PERMISSION.filter(
        (iPermission) =>
          iPermission.permissionid ===
          Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOTCREATION
      ),
    };
    if (!this.props.editButton && !this.props.vendorData) {
      this.checkPermission()
    }
  }

  componentDidMount() {
    PageTitle("MANUFACTURING_OPERATION_LOTINTIMATION");
  }

  checkPermission = () => {
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      this.setState({
        accesDenied: true
      })
    } else {
      this.fetchLotIntimationData(false);
    }
  }

  fetchLotIntimationData = (bAnimateSpinner) => {
    bAnimateSpinner && this.setState({ showLotIntimationLoadSpinner: true });
    LotIntimationProxy.GetAllLotIntimation((responseData) => {
      if (responseData) {
        const uniqueFilterValues = {
          company: Utility.getDistinctOptions(responseData, "companyid"),
          tender: Utility.getDistinctOptions(responseData, "tenderid"),
        };

        const combinedFilterValues = {
          // keeping properties same as the field name
          companyid: uniqueFilterValues.company.map(value => {
            const matchingItem = responseData.find(item => item.companyid === value);
            return matchingItem ? `${value} | ${matchingItem.companyname}` : value;
          }),
          tenderid: uniqueFilterValues.tender
            ? uniqueFilterValues.tender.map(value => {
              if (value === null) return "-";
              const matchingItem = responseData.find(item => item.tenderid === value);
              const tenderName = matchingItem && matchingItem.tendername ? matchingItem.tendername : "-";
              return `${value} | ${tenderName}`;
            })
            : ["-"],
        };
        this.setState({
          lotIntimations: responseData,
          showLotIntimationLoadSpinner: false,
          filterValues: combinedFilterValues,
        })
        this.getSummaryReport(responseData);
        this.prepareColumns(combinedFilterValues);
      } else {
        this.setState({
          errorMessage: Constants.APiFailErrorMessage,
          errorSnackbar: true,
          snackbarVariant: 'error',
          showLotIntimationLoadSpinner: false,
        })
      }
    }
    );
  };

  prepareColumns = (uniqueFilterValues) => {
    const columnNames = this.columnsWithoutPermission.map(woPermissionColumn => {
      let guids = [];

      // Determine which GUIDs (permissions) should be added based on the column field
      switch (woPermissionColumn.field) {
        case "tenderid":
          guids = [
            Constants.COLUMN_PERMISSION.MFG_INTIMATION_CARATS,
            Constants.COLUMN_PERMISSION.MFG_INTIMATION_SIZE,
            Constants.COLUMN_PERMISSION.MFG_INTIMATION_PCS,
            Constants.COLUMN_PERMISSION.MFG_INTIMATION_EXPECTED_RECEIVE,
          ];
          break;
        default:
          guids = [];
      }

      // Retrieve valid permission columns
      return this.fetchColumnDataFromGuid(guids, woPermissionColumn, this.columnsWithPermission, uniqueFilterValues);

    }).flat(); // Flatten the array

    // Update state with the prepared columns
    this.setState({
      columnNames: columnNames
    })
  };

  fetchColumnDataFromGuid = (guids, currentColumn, columnsWithPermission, uniqueFilterValues) => {
    const permissionColumns = guids
      .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
      .filter(Boolean) // Remove undefined values (invalid permissions)
      .map(guid => {
        const columnData = columnsWithPermission[guid]; // Get column details

        // If the columnData is missing or an empty object, skip adding it
        if (!columnData || Object.keys(columnData).length === 0) {
          return null;
        }

        // Retrieve unique filter values for the column, defaulting to an empty array
        const filterData = uniqueFilterValues[columnData.field] || [];

        return {
          ...columnData,  // Copy all existing column properties
          valueOptions: filterData,  // Assign filter values
        };
      })
      .filter(Boolean); // Remove any null values from the list

    // Modify the original column before adding it to the list
    const modifiedColumn = {
      ...currentColumn,  // Retain original column properties
      valueOptions: uniqueFilterValues[currentColumn.field] || [], // Assign filter values
    };

    // Return an array with the modified column and any valid permission columns
    return [modifiedColumn, ...permissionColumns];
  };

  getSummaryReport = (data) => {
    if (!Array.isArray(data)) return;
    let totalLots = data.length;
    let carats = 0;
    let totalPcs = 0;
    data.forEach((item) => {
      carats += item.carats;
      totalPcs += item.pcs;
    });

    this.setState({
      totalLot: totalLots,
      totalCarats: carats,
      totalPcs: totalPcs
    });
  }

  fetchByVendorLotIntimationData = (bAnimateSpinner) => {
    bAnimateSpinner && this.setState({ showLotIntimationLoadSpinner: true });
    DashboardProxy.GetVendorsForDashboard(
      [this.props.vendorId],
      (responseData) => {
        if (responseData) {
          this.setState({
            lotIntimations: responseData.filter(
              (res) => new Date() > new Date(res.expectedreceive)
            ),
            showLotIntimationLoadSpinner: false,
          });
        }
      }
    );
  };

  handleUpload = (data, intimationid) => {
    this.setState({ uploadLoader: true });
    LotIntimationProxy.UploadDocument(intimationid, data, (response) => {
      if (response) {
        this.setState({
          showAddLotDocumentModal: false,
          uploadResponse: response,
          uploadSuccessSnackbar: true,
          uploadLoader: false,
          errorSnackbar: true,
          errorMessage: response.errorMessage,
          snackbarVariant: "success",
        })
      } else {
        this.setState({
          errorSnackbar: true,
          errorMessage: Constants.APiFailErrorMessage,
          snackbarVariant: "error",
          loadingButton: false,
        });
      }
    });
  };

  handleClose = () => {
    this.setState({ uploadSuccessSnackbar: true });
  };

  columnsWithPermission = {
    "867f47d5-8537-4112-beb4-9e59ebd70296": {
      field: "carats",
      headerName: "CARATS",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: 'number',
      flex: 0.5,
      renderCell: renderCellExpand,
    },
    "3d32fc9f-6272-4c37-a9b7-d79dcee49b64": {
      field: "size",
      headerName: "SIZE",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      flex: 0.5,
      renderCell: renderCellExpand,
    },
    "bf8ac759-8a6a-4c7c-a58a-720cbfed0a75": {
      field: "pcs",
      headerName: "PCS",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: 'number',
      flex: 0.5,
      renderCell: renderCellExpand,
    },
    "16040e4e-6152-42b2-b043-14b29eee958b": {
      field: "expectedreceive",
      headerName: "EXPECTED RECEIVE",
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
      type: "date",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => {
        if (params.value) {
          return Utility.ConvertUtcToLocalDate(params.value);
        }
      }
    },
  }

  columnsWithoutPermission = [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: 'number',
      flex: 0.5,
    },
    {
      field: "companyid",
      headerName: "COMPANY",
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
      type: "singleSelect",
      filterOperators: Utility.combinedFilters(),
      valueGetter: (params) => {
        if (params.row.companyid && params.row.companyname) {
          return `${params.row.companyid} | ${params.row.companyname}`;
        }
        return "";
      },
      renderCell: (params) => {
        if (params.row.companyid && params.row.companyname) {
          return `${params.row.companyid} | ${params.row.companyname}`;
        }
        return "";
      },
      disableExport: true,
    },
    {
      field: "tenderid",
      headerName: "TENDER",
      headerAlign: "center",
      align: "center",
      width: 200,
      type: "singleSelect",
      filterOperators: Utility.combinedFilters(),
      valueGetter: (params) => {
        if (!params.row.tenderid && !params.row.tendername) {
          return "-";
        }
        const tenderName = params.row.tendername || "-";
        return `${params.row.tenderid} | ${tenderName}`;
      },
      renderCell: (params) => {
        if (!params.row.tenderid && !params.row.tendername) {
          return "-";
        }
        const tenderName = params.row.tendername || "-";
        return `${params.row.tenderid} | ${tenderName}`;
      },
      disableExport: true,
    },
    {
      field: "received",
      headerName: "RECEIVED",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return this.props.vendorData ? (
          params.value ? (
            "True"
          ) : (
            "False"
          )
        ) : Utility.isUpdateAllowed(this.state.lotCreation_Permission) ||
          Utility.isAddAllowed(this.state.lotCreation_Permission) ? (
          <Button
            variant="contained"
            onClick={(e) => {
              e.stopPropagation()
              this.setState({
                showAddLotCreationModal: true,
                editOperation: true,
                sourceData: {
                  ...params.row,
                  lotintimationid: params.row.id,
                  datereceived: params.row.expectedreceive,
                  id: undefined,
                },
                loadingButton: false,
              });
            }}
          >
            Receive
          </Button>
        ) : <CloseIcon color="error" />;
      },
    },
    {
      field: "document",
      headerName: "DOCUMENT",
      headerAlign: "center",
      align: "center",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            style={{ textAlign: "center", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation()
              this.setState({
                intimationid: params.row.id,
                showAddLotDocumentModal: true,
                loadingButton: false
              })
            }}
          >
            {" "}
            <IoDocumentAttach color="primary" size={25} />
          </Box>
        )
      },
    },
    {
      field: "edit",
      headerName: "EDIT",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            style={{ textAlign: "center", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation()
              this.setState({
                showAddLotIntimationModal: true,
                editOperation: true,
                sourceData: params.row,
                loadingButton: false,
              });
            }}
          >
            <BorderColorIcon color="primary" />
          </Box>
        );
      },
    },
  ];

  hideAddLotIntimationModal = () => {
    this.setState({
      showAddLotIntimationModal: false,
    });
  };

  hideShowDetailModal = () => {
    this.setState({
      showDetailModal: false,
    });
  };

  hideAddDocumentModal = () => {
    this.setState({
      showAddLotDocumentModal: false,
    });
  };

  handleValidation = (lotIntimationData) => {
    //TODO do validation if needed here
    return true;
  };

  addLotIntimation = (lotIntimationData) => {
    this.setState({ loadingButton: true });
    if (this.state.editOperation) {
      //Update existing LotIntimation, and once lotIntimation is updated on call refresh the list of current lotIntimations
      LotIntimationProxy.UpdateLotIntimation(lotIntimationData, (response) => {
        if (response) {
          this.fetchLotIntimationData(true);
          this.setState({ loadingButton: false });
          //Hidding lotIntimation modal
          this.hideAddLotIntimationModal();
        } else {
          this.setState({
            errorSnackbar: true,
            errorMessage: Constants.APiFailErrorMessage,
            snackbarVariant: "error",
            loadingButton: false,
          });
        }
      });
    } else {
      //Creating new LotIntimation, and once lotIntimation is created on call refresh the list of current lotIntimations
      LotIntimationProxy.AddLotIntimation(lotIntimationData, (response) => {
        if (response) {
          this.fetchLotIntimationData(true);
          this.setState({ loadingButton: false });
          //Hidding lotIntimation modal
          this.hideAddLotIntimationModal();
        } else {
          this.setState({
            errorSnackbar: true,
            errorMessage: Constants.APiFailErrorMessage,
            snackbarVariant: "error",
            loadingButton: false,
          });
        }
      });
    }
  };

  addLotCreation = (lotCreationData) => {
    this.setState({ loadingButton: true });
    //Update existing LotCreation, and once lotCreation is updated on call refresh the list of current lotCreations
    LotCreationProxy.AddLotCreation(lotCreationData, (response) => {
      if (response) {
        this.setState({
          loadingButton: false,
          showAddLotCreationModal: false,
        });
        this.setSnackbar(true, response.errorMessage, response.status);
        this.fetchLotIntimationData(true);
      } else {
        this.setState({
          loadingButton: false,
          showAddLotCreationModal: false,
        });
        this.setSnackbar(true, Constants.APiFailErrorMessage, "error");
      }
    });
  };

  handleRowClick = (params) => {
    let id = params.row.id;
    this.setState({
      lotid: id,
      showDetailModal: true,
    });
  };

  setSnackbar = (showSnackbar, snackbarMessage, snackbarVariant) => {
    this.setState({
      showSnackbar: showSnackbar,
      snackbarMessage: snackbarMessage,
      snackbarVariant: snackbarVariant
    });
  }

  render() {
    const CustomToolbar = () => {
      return (
        <GridToolbarContainer
          sx={{
            display: "flex",
            alignItems: "center",
            marginHorizontal: "5px",
            "&.MuiDataGrid-toolbarContainer": {
              padding: "0px 4px !important",
            },
          }}
        >
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarExport />
          <Box
            sx={{
              flexGrow: 2,
              flexBasis: 0,
              overflow: "hidden",
              color: "black",
              fontWeight: "normal",
              textAlign: "center",
              minWidth: "250px",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              flexWrap="wrap"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: Constants.defaultTableFontSize }}>
                Total Lot: <b>{this.state.totalLot}</b>
              </Typography>
              <Typography sx={{ fontSize: Constants.defaultTableFontSize }}>
                Total Carats: <b>{this.state.totalCarats.toFixed(2)}</b>
              </Typography>
              <Typography sx={{ fontSize: Constants.defaultTableFontSize }}>
                Total Pcs: <b>{this.state.totalPcs}</b>
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ flexGrow: 1, display: "block" }} />
          {this.state.showLotIntimationLoadSpinner ? (
            <CircularProgress size="1rem" />
          ) : (
            <Tooltip title="Reload List">
              <RefreshOutlinedIcon
                color="primary"
                fontSize="medium"
                onClick={() => this.fetchLotIntimationData(true)}
                sx={{ cursor: "pointer" }}
              />
            </Tooltip>
          )}
          {Utility.isAddAllowed(this.state.local_Permission) &&
            !this.props.editButton ? (
            <Tooltip title="Add lot Intimation">
              <Button
                color="info"
                variant="outlined"
                size="small"
                sx={{ ml: "5px" }}
                onClick={() =>
                  this.setState({
                    showAddLotIntimationModal: true,
                    editOperation: false,
                    sourceData: {},
                    loadingButton: false,
                  })
                }
                startIcon={<AddBoxOutlinedIcon size="large" />}
              >
                Lot Intimation
              </Button>
            </Tooltip>
          ) : null}
          <GridToolbarQuickFilter debounceMs={500} />
        </GridToolbarContainer>
      )
    }


    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed && !this.props.editButton && !this.props.vendorData) {
      return <NoPermission />
    }
    const rows = this.state.lotIntimations;
    const col = this.props.vendorData
      ? this.initialColumns().filter((col) => col.field !== "edit")
      : Utility.isUpdateAllowed(this.state.local_Permission) &&
        !this.props.editButton
        ? this.state.columnNames
        : this.state.columnNames.filter(
          (col) => col.field !== "edit" && col.field !== "received"
        );
    const columns = this.props.vendorData
      ? col.filter((col) => col.field !== "upload")
      : Utility.isUpdateAllowed(this.state.local_Permission) &&
        !this.props.editButton
        ? col
        : col.filter(
          (col) => col.field !== "edit" && col.field !== "received"
        );
    return (
      <Box>
        {this.state.showAddLotCreationModal ? (
          <AddLotCreationModal
            Show={true}
            handleClose={() =>
              this.setState({ showAddLotCreationModal: false })
            }
            handleSave={this.addLotCreation}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
            disabled={true}
          />
        ) : (
          <Box />
        )}
        <CommonSnackbar
          show={this.state.errorSnackbar}
          message={<Box>{this.state.errorMessage}</Box>}
          variant={this.state.snackbarVariant}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => this.setState({ errorSnackbar: false })}
          title={
            this.state.snackbarVariant === "success" ? "Success" : "Failed"
          }
        />

        <CommonSnackbar
          show={this.state.showSnackbar}
          message={this.state.snackbarMessage}
          variant={this.state.snackbarVariant}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => this.setState({ showSnackbar: false })}
          title={this.state.snackbarVariant === "success" ? "Success" :
            this.state.snackbarVariant === "error" ? "Failed" : "Warning"}
        />

        {this.state.showAddLotIntimationModal ? (
          <AddLotIntimationModal
            Show={true}
            handleClose={this.hideAddLotIntimationModal}
            handleSave={this.addLotIntimation}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
          />
        ) : (
          <Box />
        )}
        {this.state.showAddLotDocumentModal ? (
          <UploadAndViewLotDocumentModal
            show={true}
            loader={this.state.loadingButton}
            handleSave={this.handleUpload}
            handleClose={this.hideAddDocumentModal}
            intimationid={this.state.intimationid}
          />
        ) : (
          <Box />
        )}

        <Box px={1}>
          <Box
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              width: "100%",
              position: "relative",
              "& .MuiTablePagination-selectLabel": {
                marginTop: "15px",
              },
              "& .MuiTablePagination-displayedRows": {
                marginTop: "15px",
              },
              "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                fontWeight: Constants.defaultFontWeight,
              },
              "& .css-cc8tf1": {
                fontWeight: Constants.defaultFontWeight,
              },
              height: !this.props.editButton ? "83vh" : "",
            }}
          >
            <DataGridPro
              disableRowSelectionOnClick={true}
              getRowHeight={() => "auto"}
              autoHeight={!this.props.editButton ? false : true}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  color: '#fff',
                  backgroundColor: 'grey',
                },
                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                  py: "4px",
                },
                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                  py: "12px",
                },
                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                  py: "18px",
                },

                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
              }}
              density="compact"
              slots={{ toolbar: CustomToolbar }}
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              onRowClick={this.handleRowClick}
              loading={this.state.showLotIntimationLoadSpinner}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    // Hide columns status and traderName, the other columns will remain visible
                    locationid: false,
                  },
                },
                pagination: {
                  paginationModel: Constants.pageSize,
                },
              }}
              pageSizeOptions={Constants.pageSizeOptions}
              pagination
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }
}

export default LotIntimationMaster;
